import {
  ChangeEventHandler,
  FC,
  Key,
  useCallback,
  useEffect,
  useMemo,
} from "react";

import { useAppDispatch, useAppSelector } from "@/store/app/hooks";
import {
  accessTokenSelector,
  accountDataSelector,
  refreshTokenSelector,
  userDataSelector,
} from "@/store/features/account/accountSelectors";
import {
  accountsSelector,
  activeTemplateDataSelector,
  emailDataSelector,
  emailSearchSelector,
  emailStatusSelector,
  loadingEmailSelector,
  recieverEmailSelector,
  senderEmailKeySelector,
} from "@/store/features/email/emailSelectors";

import { StyledButton } from "@/components/styled-button";
import { TemplateSelector } from "./TemplateSelector";
import { AccountSelectField } from "./AccountSelectField";
import { ReactComponent as SendIcon } from "@/assets/icons/send.svg";
import { ReactComponent as CloseIcon } from "@/assets/icons/close.svg";

import { sendEmailAsync } from "@/store/features/email/emailAsyncThunks";
import {
  setActiveTemplateData,
  setRecieverEmail,
  setSenderEmailKey,
} from "@/store/features/email/emailSlice";
import { AccountInputField } from "./AccountInputField";
import { reformatText, sendUserAnalytics } from "@/modules/server";
import { EmailSendStatus } from "@/constants/email-send-status";
import { sendAmplitudeData } from "@/analytics";
import { AMPLITUDE_EVENTS } from "@/analytics/events";
import { getLocalISODate, toMilliseconds } from "@/utils/formatDate";
import {
  loadDataSelector,
  sourceSelector,
} from "@/store/features/load-info/loadInfoSelectors";
import { TextEditorQuill } from "./TextEditorQuill";
import { isBrokerSelector } from "@/store/features/tabs/tabsSelectors";
import { MC_NUMBER_REGEX } from "@/constants/regex";

export const EmailView: FC = () => {
  const dispatch = useAppDispatch();

  const accessToken = useAppSelector(accessTokenSelector);
  const refreshToken = useAppSelector(refreshTokenSelector);
  const activeTemplateData = useAppSelector(activeTemplateDataSelector);
  const accounts = useAppSelector(accountsSelector);
  const loading = useAppSelector(loadingEmailSelector);
  const accountData = useAppSelector(accountDataSelector);
  const emailData = useAppSelector(emailDataSelector);
  const emailStatus = useAppSelector(emailStatusSelector);
  const loadData = useAppSelector(loadDataSelector);
  const source = useAppSelector(sourceSelector);
  const userData = useAppSelector(userDataSelector);
  const isBroker = useAppSelector(isBrokerSelector);
  const search = useAppSelector(emailSearchSelector);

  const senderEmailKey = useAppSelector(senderEmailKeySelector);
  const recieverEmail = useAppSelector(recieverEmailSelector);

  const getAccountData = useCallback(
    (key?: Key | null) =>
      accounts?.find((account) => account.id === Number(key)),
    [accounts]
  );

  const isAllowToSend = useMemo(
    () =>
      getAccountData(senderEmailKey)?.verified &&
      !!senderEmailKey &&
      !!recieverEmail &&
      !!activeTemplateData,
    [activeTemplateData, getAccountData, recieverEmail, senderEmailKey]
  );

  const reformatAllText = useCallback(
    (text?: string) => {
      if (!text) return "";

      return reformatText(text, isBroker, emailData, search);
    },
    [emailData, isBroker, search]
  );

  const sendAnalytics = useCallback(() => {
    if (!emailData || !loadData?.age || !loadData.rpm) return;

    const publishedTime = Date.now() - toMilliseconds(loadData?.age);

    sendUserAnalytics(accessToken, refreshToken, {
      publishedTime: loadData?.age ? getLocalISODate(publishedTime) : "",
      contactTime: getLocalISODate(),
      origin: emailData.origin,
      destination: emailData.dest,
      tripMiles: emailData.trip,
      truckType: emailData.truck,
      companyName: emailData.company,
      contact: emailData.email,
      rate: emailData.rate || 0,
      rpm: loadData?.rpm || 0,
      typeOfContact: "EMAIL",
      source: source || "",
      userId: userData?.userId || 0,
    });
  }, [
    accessToken,
    emailData,
    loadData?.age,
    loadData?.rpm,
    refreshToken,
    source,
    userData?.userId,
  ]);

  const sendEmail = () => {
    if (emailStatus === EmailSendStatus.EMAIL_SENT) {
      window.parent.postMessage("closeldcmodal", "*");
    } else if (isAllowToSend) {
      sendAmplitudeData(AMPLITUDE_EVENTS.popupSendEmailClicked);
      sendAnalytics();

      const mc =
        activeTemplateData?.subject.match(MC_NUMBER_REGEX)?.[0] ||
        activeTemplateData?.template.match(MC_NUMBER_REGEX)?.[0] ||
        "";

      dispatch(
        sendEmailAsync({
          toEmail: recieverEmail || "",
          account: getAccountData(senderEmailKey)?.email as string,
          tokens: { accessToken, refreshToken },
          subject: reformatAllText(activeTemplateData?.subject),
          cc: activeTemplateData?.cc || "",
          template: reformatAllText(activeTemplateData?.template),
          isBroker,
          search: search
            ? {
                ...search,
                mc,
              }
            : null,
        })
      );
    }
  };

  const handleSubjectChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      dispatch(setActiveTemplateData({ subject: e.target.value }));
    },
    [dispatch]
  );

  const handleCKEditorChange = useCallback(
    (value: string) => {
      sendAmplitudeData(AMPLITUDE_EVENTS.popupEmailBodyValueChanged);
      dispatch(setActiveTemplateData({ template: value }));
    },
    [dispatch]
  );

  const handleSenderChange = useCallback(
    (key: Key | null) => {
      dispatch(setSenderEmailKey(key));
      sendAmplitudeData(AMPLITUDE_EVENTS.popupEmailSenderSwitched);
    },
    [dispatch]
  );

  const initialText = useMemo(
    () => reformatAllText(activeTemplateData?.template),
    [activeTemplateData?.template, reformatAllText]
  );

  const initialSubject = useMemo(
    () => reformatAllText(activeTemplateData?.subject),
    [activeTemplateData?.subject, reformatAllText]
  );

  useEffect(() => {
    if (accountData) dispatch(setSenderEmailKey(accountData.id));
  }, [accountData, dispatch]);

  const sendButtonIcon = useMemo(
    () =>
      emailStatus === EmailSendStatus.EMAIL_SENT ? <CloseIcon /> : <SendIcon />,
    [emailStatus]
  );

  const sendButtonTitle = useMemo(() => {
    switch (emailStatus) {
      case EmailSendStatus.NONE:
        return "Send";
      case EmailSendStatus.EMAIL_SENT:
        return "Email Successfully Sent";
      case EmailSendStatus.ERROR:
        return "Error";
      case EmailSendStatus.UNAUTHORIZED:
        return "Unauthorized";
      case EmailSendStatus.VERIFIFICATION_REQUIRED:
        return "Verification Required";
      case EmailSendStatus.RECHARGE:
        return "Recharge";
      default:
        return "Send";
    }
  }, [emailStatus]);

  return (
    <div className="flex flex-col gap-3 h-full">
      <div className="flex gap-3 w-full">
        <AccountSelectField
          options={accounts}
          placeholder="Type email"
          label="From"
          onSelectionChange={handleSenderChange}
          selectedKey={senderEmailKey?.toString()}
        />
        <AccountInputField
          placeholder="Type email"
          label="To"
          onValueChange={(value) => dispatch(setRecieverEmail(value))}
          value={recieverEmail}
        />
      </div>
      <div className="border-1 rounded-lg border-gray-200 h-full flex">
        <TemplateSelector />
        <div className="w-full h-full flex flex-col">
          <input
            className="outline-none w-full h-11 border-b-1 border-gray-200 text-sm font-semibold p-3"
            type="text"
            value={initialSubject}
            onChange={handleSubjectChange}
          />
          {emailData && (
            // <TextEditor onChange={handleCKEditorChange} value={initialText} />
            <TextEditorQuill
              onChange={handleCKEditorChange}
              value={initialText}
            />
          )}
        </div>
      </div>
      <div className="flex justify-end items-center gap-2">
        <StyledButton
          color="primary"
          endContent={sendButtonIcon}
          onPress={sendEmail}
          isDisabled={!isAllowToSend}
          isLoading={loading}
        >
          {sendButtonTitle}
        </StyledButton>
      </div>
    </div>
  );
};
